// ═══════════════════════════════════════════════════════════
// GabSelect — Searchable dropdown with keyboard navigation
// Replaces Mantine's <Select> and <MultiSelect>
// ═══════════════════════════════════════════════════════════

import { useState, useRef, useEffect, useCallback } from 'react';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface GabSelectProps {
  options: SelectOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  label?: string;
  placeholder?: string;
  searchable?: boolean;
  clearable?: boolean;
  required?: boolean;
  error?: string;
  help?: string;
  disabled?: boolean;
  className?: string;
}

export function GabSelect({
  options, value, onChange, label, placeholder = 'Select...', searchable = true,
  clearable, required, error, help, disabled, className = '',
}: GabSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlighted, setHighlighted] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = options.find(o => o.value === value);
  const filtered = search
    ? options.filter(o => (o.label || '').toLowerCase().includes(search.toLowerCase()))
    : options;

  // Close on click outside
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const select = useCallback((val: string) => {
    onChange(val);
    setOpen(false);
    setSearch('');
    setHighlighted(-1);
  }, [onChange]);

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted(h => Math.min(h + 1, filtered.length - 1));
      if (!open) setOpen(true);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter' && highlighted >= 0 && filtered[highlighted]) {
      e.preventDefault();
      select(filtered[highlighted].value);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setSearch('');
    }
  }

  const inputId = label ? label.toLowerCase().replace(/\s+/g, '-') : undefined;

  return (
    <div className={`form-group ${className}`} ref={containerRef} style={{ position: 'relative' }}>
      {label && <label className={`form-label${required ? ' required' : ''}`} htmlFor={inputId}>{label}</label>}

      <div style={{ position: 'relative' }}>
        {searchable ? (
          <input
            ref={inputRef}
            className={`form-input${error ? ' error' : ''}`}
            id={inputId}
            value={open ? search : (selected?.label || '')}
            placeholder={placeholder}
            disabled={disabled}
            onFocus={() => { setOpen(true); setSearch(''); }}
            onChange={e => { setSearch(e.target.value); setHighlighted(0); if (!open) setOpen(true); }}
            onKeyDown={handleKeyDown}
            autoComplete="off"
          />
        ) : (
          <button
            className={`form-input${error ? ' error' : ''}`}
            style={{ textAlign: 'left', cursor: 'pointer', background: 'white' }}
            disabled={disabled}
            onClick={() => setOpen(!open)}
            onKeyDown={handleKeyDown}
          >
            {selected?.label || <span style={{ color: 'var(--color-neutral-400)' }}>{placeholder}</span>}
          </button>
        )}

        {clearable && value && (
          <button
            onClick={(e) => { e.stopPropagation(); onChange(null); setSearch(''); }}
            style={{
              position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
              background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-neutral-400)',
              fontSize: 16, lineHeight: 1, padding: 4,
            }}
            aria-label="Clear"
          >
            &times;
          </button>
        )}
      </div>

      {open && filtered.length > 0 && (
        <div
          style={{
            position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
            background: 'white', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)',
            maxHeight: 240, overflowY: 'auto', zIndex: 'var(--z-dropdown)' as any,
            border: '1px solid var(--color-neutral-200)',
          }}
          role="listbox"
        >
          {filtered.map((opt, i) => (
            <div
              key={opt.value}
              role="option"
              aria-selected={opt.value === value}
              style={{
                padding: 'var(--spacing-2) var(--spacing-3)',
                cursor: opt.disabled ? 'not-allowed' : 'pointer',
                background: i === highlighted ? 'var(--color-primary-50)' : opt.value === value ? 'var(--color-neutral-50)' : 'white',
                fontWeight: opt.value === value ? 600 : 400,
                fontSize: 'var(--font-size-sm)',
                opacity: opt.disabled ? 0.5 : 1,
              }}
              onMouseEnter={() => setHighlighted(i)}
              onClick={() => !opt.disabled && select(opt.value)}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}

      {open && filtered.length === 0 && search && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
          background: 'white', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)',
          padding: 'var(--spacing-3)', textAlign: 'center', color: 'var(--color-neutral-500)',
          fontSize: 'var(--font-size-sm)', zIndex: 'var(--z-dropdown)' as any,
          border: '1px solid var(--color-neutral-200)',
        }}>
          No results
        </div>
      )}

      {help && !error && <span className="form-help">{help}</span>}
      {error && <span className="form-error" role="alert">{error}</span>}
    </div>
  );
}
